import type { BenchmarkSelection, CompanySlug, ModelView } from "../shared/contracts.ts";
import { resolveModelScore } from "../shared/benchmark-observation.ts";

export interface CapabilityChange {
  companySlug: CompanySlug;
  /** UTC release day, YYYY-MM-DD. */
  date: string;
  score: number;
  previousScore: number | null;
  /** Tied daily leaders, sorted by ID; names use the same order. */
  modelIds: string[];
  modelNames: string[];
}

function timestamp(value: string | null): number {
  if (!value) return Number.NaN;
  // Reject invalid calendar days rather than accepting Date.parse's rollover.
  const day = /^\d{4}-\d{2}-\d{2}(?=$|T)/.exec(value)?.[0];
  if (!day) return Number.NaN;
  const midnight = Date.parse(`${day}T00:00:00.000Z`);
  if (!Number.isFinite(midnight) || new Date(midnight).toISOString().slice(0, 10) !== day) return Number.NaN;
  return Date.parse(value);
}

/** All-history measured records, independent of display windows and pricing. */
export function buildCapabilityChanges(
  models: ModelView[],
  { referenceDate, selection }: { referenceDate: string; selection: BenchmarkSelection },
): CapabilityChange[] {
  const cutoff = timestamp(referenceDate);
  if (!Number.isFinite(cutoff)) return [];
  const days = new Map<CompanySlug, Map<string, { score: number; models: ModelView[] }>>();

  for (const model of models) {
    const observation = resolveModelScore(model, selection);
    if (!observation || observation.estimated || !Number.isFinite(observation.value)) continue;
    const official = timestamp(model.officialReleaseDate);
    const releasedAt = Number.isFinite(official) ? official : timestamp(model.catalogedAt);
    if (!Number.isFinite(releasedAt) || releasedAt > cutoff) continue;
    const date = new Date(releasedAt).toISOString().slice(0, 10);
    const companyDays = days.get(model.companySlug) ?? new Map();
    days.set(model.companySlug, companyDays);
    const current = companyDays.get(date);
    if (!current || observation.value > current.score) {
      companyDays.set(date, { score: observation.value, models: [model] });
    } else if (observation.value === current.score) {
      current.models.push(model);
    }
  }

  const changes: CapabilityChange[] = [];
  for (const [companySlug, companyDays] of days) {
    let previousScore: number | null = null;
    for (const [date, day] of [...companyDays].sort(([left], [right]) => left.localeCompare(right))) {
      if (previousScore !== null && day.score <= previousScore) continue;
      const leaders = day.models.sort((left, right) => left.id.localeCompare(right.id));
      changes.push({
        companySlug,
        date,
        score: day.score,
        previousScore,
        modelIds: leaders.map((model) => model.id),
        modelNames: leaders.map((model) => model.shortName),
      });
      previousScore = day.score;
    }
  }
  return changes.sort((left, right) => left.date.localeCompare(right.date) || left.companySlug.localeCompare(right.companySlug));
}
